/* eslint-disable no-nested-ternary */
import { useEffect, useMemo, useRef, useState } from 'react';

import { LoaderFunctionArgs, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import {
  Button,
  Center,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spacer,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useBoolean,
  useDisclosure,
} from '@chakra-ui/react';

import { QueryClient, useQueryClient } from '@tanstack/react-query';

import {
  Card,
  IndividualRole,
  ArrowLeftOutline,
  ChevronDownOutline,
  DeleteOutline,
  EditOutline,
} from '@aslanpay/portal-common-lib';

import PauseEmployeeModal from 'app/components/Employees/PauseEmployee/PauseEmployeeModal';
import ReactivateEmployeeModal from 'app/components/Employees/ReactivateEmployee/ReactivateEmployeeModal';
import DeleteIndividualModal from 'app/components/Individuals/DeleteIndividual/DeleteIndividualModal';
import InviteIndividualModal from 'app/components/Individuals/InviteIndividual/InviteIndividualModal';
import ReassignPayCalendarModal from 'app/components/Individuals/IndividualProfile/IndividualPayrollTab/ReassignPayCalendarModal';
import AuthenticatedPageWrapper from 'app/components/UI/AuthenticatedPageWrapper';

import {
  IndividualDeductionsReportsTab,
  IndividualPayrollTabEdit,
  IndividualPayrollTabView,
  IndividualPointsTab,
  IndividualProfileTab,
  IndividualWalletTab,
} from 'app/components/Individuals/IndividualProfile';

import useFetchCards from 'app/hooks/useFetchCards';
import useFetchCompany from 'app/hooks/useFetchCompany';
import useFetchIndividual, { fetchIndividualQuery } from 'app/hooks/useFetchIndividual';
import useFetchPayCalendar from 'app/hooks/useFetchPayCalendar';
import useFetchPayCalendars from 'app/hooks/useFetchPayCalendars';
import useFetchWallets from 'app/hooks/useFetchWallets';

import { GrantAccessButton } from 'app/components/Employees/GrantAccess';
import { RestrictAccessButton } from 'app/components/Employees/RestrictAccess';
import IndividualEarningsTab from 'app/components/Individuals/IndividualProfile/IndividualEarningsTab';
import IndividualDeductionRequestsTab from 'app/components/Individuals/IndividualProfile/IndividualDeductionRequestsTab';
import useFetchCompanyConfiguration from 'app/hooks/useFetchCompanyConfiguration';
import useFetchIndividualConfiguration from 'app/hooks/useFetchIndividualConfigurations';
import { getAdminsPath, getEmployeesPath } from 'app/utils/routes';
import { VoucherTable } from 'app/components/VoucherTable';

import getIndividualProfileTabs from './tabsConfig';

export const individualProfileLoader =
  (queryClient: QueryClient) =>
  async ({ params }: LoaderFunctionArgs) => {
    const query = fetchIndividualQuery(params.individualId);
    return queryClient.getQueryData(query.queryKey) ?? (await queryClient.fetchQuery(query));
  };

function IndividualProfile() {
  const submitButtonRef = useRef<HTMLDivElement>(null);

  const navigate = useNavigate();
  const handleClickBack = () => navigate(-1);

  const [searchParams, setSearchParams] = useSearchParams();
  const { individualId } = useParams();

  const queryClient = useQueryClient();

  const {
    isOpen: isInviteIndividualOpen,
    onOpen: onOpenInviteIndividual,
    onClose: onCloseInviteIndividual,
  } = useDisclosure();

  const {
    isOpen: isPauseIndividualOpen,
    onOpen: onOpenPauseIndividual,
    onClose: onClosePauseIndividual,
  } = useDisclosure();

  const {
    isOpen: isReactivateIndividualOpen,
    onOpen: onOpenReactivateIndividual,
    onClose: onCloseReactivateIndividual,
  } = useDisclosure();

  const {
    isOpen: isDeleteIndividualOpen,
    onOpen: onOpenDeleteIndividual,
    onClose: onCloseDeleteIndividual,
  } = useDisclosure();

  const {
    isOpen: isReassignPayCalendarOpen,
    onOpen: onOpenReassignPayCalendar,
    onClose: onCloseReassignPayCalendar,
  } = useDisclosure();

  const { data: individual, isInitialLoading: isLoadingIndividual } =
    useFetchIndividual(individualId);

  const { data: company, isInitialLoading: isLoadingCompany } = useFetchCompany(
    individual?.companyId
  );

  const { data: companyConfiguration } = useFetchCompanyConfiguration(company?.id);

  const { data: individualConfiguration } = useFetchIndividualConfiguration(
    individualId,
    individual?.role === 'EMPLOYEE'
  );

  const { data: payCalendar, isInitialLoading: isLoadingPayCalendar } = useFetchPayCalendar(
    individual?.payCalendarId
  );

  // "Change pay calendar" only makes sense if there's somewhere else to move the worker to —
  // hide it when the company has just the one (their current) active pay calendar.
  const { data: companyPayCalendarsPage } = useFetchPayCalendars(
    {
      companyId: individual?.companyId ? [individual.companyId] : undefined,
      status: ['ACTIVE'],
      page: 0,
      size: 999999,
    },
    !!individual?.companyId
  );
  const hasMultiplePayCalendars = (companyPayCalendarsPage?.total ?? 0) > 1;

  const { data: wallets, isInitialLoading: isLoadingWallets } = useFetchWallets(
    {
      employeeIds: [individual?.id || ''],
      page: 0,
      size: 1,
    },
    !!individual?.id && individual.role === 'EMPLOYEE'
  );

  const wallet = wallets?.list[0];

  const isReserveEnabled = companyConfiguration?.modulesConfig.modules.RESERVE === 'ENABLED';
  const isSavingsEnabled = companyConfiguration?.modulesConfig.modules.SAVINGS === 'ENABLED';

  const [shouldPoolCard, setShouldPoolCard] = useBoolean();

  const { data: cards, isInitialLoading: isLoadingCards } = useFetchCards(
    {
      walletIds: [wallet?.id || ''],
      page: 0,
      size: 1,
    },
    !!wallet?.id && individual?.role === 'EMPLOYEE',
    shouldPoolCard
  );

  const card = cards?.list[0] as Card & {
    statusChangedByName: string;
    statusChangedByRole: IndividualRole;
  };

  const tabsConfig = useMemo(
    () => getIndividualProfileTabs(individual, isReserveEnabled, isSavingsEnabled),
    [individual, isReserveEnabled, isSavingsEnabled]
  );

  const currentTabLabel = searchParams.get('tab') || tabsConfig[0].label;

  const [editMode, setEditMode] = useBoolean();
  const [tabLabel, setTabLabel] = useState(currentTabLabel);

  const isEmployee = individual?.role === 'EMPLOYEE';

  const handleToggleEditMode = () => {
    if (tabLabel === 'payroll' && individual?.payCalendarId) {
      setTabLabel('payroll');
    } else {
      setTabLabel('profile');
    }

    setEditMode.toggle();
  };

  const handleDelete = () => {
    const getIndividualsPath = isEmployee ? getEmployeesPath : getAdminsPath;

    navigate(getIndividualsPath());
  };

  const handleInvalidateIndividualQuery = () => {
    queryClient.invalidateQueries({ queryKey: ['fetchIndividual', individualId] });
  };

  useEffect(() => {
    setSearchParams(
      (prev) => {
        const currentTab = prev.get('tab');

        if (currentTab === tabLabel) return prev;

        const params = new URLSearchParams(prev);
        params.set('tab', tabLabel);

        return params;
      },
      { replace: true }
    );
  }, [tabLabel, setSearchParams]);

  useEffect(() => {
    if (
      card &&
      [
        'PENDING_ACTIVATION',
        'ACTIVATING',
        'BLOCKING',
        'SUSPENDING',
        'LOCKING',
        'UNLOCKING',
        'DELETING',
      ].includes(card?.cardStatus)
    ) {
      setShouldPoolCard.on();
    } else {
      setShouldPoolCard.off();
    }
  }, [card, setShouldPoolCard]);

  const visibleTabs = useMemo(
    () => tabsConfig.filter((tabConfig) => tabConfig.visible),
    [tabsConfig]
  );

  const handleTabsChange = (index: number) => {
    if (visibleTabs[index]) {
      setTabLabel(visibleTabs[index].label);
    }
  };

  return (
    <AuthenticatedPageWrapper title="Individual Profile">
      {isLoadingIndividual ||
      isLoadingCompany ||
      isLoadingPayCalendar ||
      isLoadingWallets ||
      isLoadingCards ? (
        <Center width="full" height="full" flexGrow={1}>
          <Spinner color="brand.purple" size="xl" mb="50px" />
        </Center>
      ) : (
        <>
          <InviteIndividualModal
            selectedIndividuals={[individual?.id || '']}
            isOpen={isInviteIndividualOpen}
            onClose={onCloseInviteIndividual}
            onInvite={handleInvalidateIndividualQuery}
            type={isEmployee ? 'employee' : 'admin'}
          />
          <PauseEmployeeModal
            id={individual?.id || ''}
            isOpen={isPauseIndividualOpen}
            onClose={onClosePauseIndividual}
            onPause={handleInvalidateIndividualQuery}
          />
          <ReactivateEmployeeModal
            id={individual?.id || ''}
            isOpen={isReactivateIndividualOpen}
            onClose={onCloseReactivateIndividual}
            onReactivate={handleInvalidateIndividualQuery}
          />
          <DeleteIndividualModal
            selectedIndividuals={[individual?.id || '']}
            isOpen={isDeleteIndividualOpen}
            onClose={onCloseDeleteIndividual}
            onDelete={handleDelete}
            type={isEmployee ? 'employee' : 'admin'}
          />
          <ReassignPayCalendarModal
            isOpen={isReassignPayCalendarOpen}
            onClose={onCloseReassignPayCalendar}
            individual={individual}
          />
          <Button
            my="10px"
            alignSelf="flex-start"
            size="sm"
            variant="link"
            leftIcon={<ArrowLeftOutline boxSize="20px" />}
            onClick={handleClickBack}
          >
            Back
          </Button>
          <Flex gap="20px" alignItems="flex-start" ref={submitButtonRef}>
            <Text as="h2" textStyle="h2" color="brand.darkPurple">
              {individual?.firstName
                ? `${individual?.firstName} ${individual?.lastName}`
                : individual?.email}
            </Text>
            <Spacer />
            {!editMode && (
              <>
                <Menu autoSelect={false} matchWidth>
                  <MenuButton
                    as={Button}
                    variant="outline"
                    size="sm"
                    height="40px"
                    rightIcon={<ChevronDownOutline boxSize="20px" />}
                  >
                    More actions
                  </MenuButton>
                  <MenuList minW={0}>
                    <MenuItem
                      isDisabled={
                        individual?.status !== 'CREATED' && individual?.status !== 'INVITED'
                      }
                      onClick={onOpenInviteIndividual}
                    >
                      Send invitation
                    </MenuItem>
                    {individual?.role === 'EMPLOYEE' && (
                      <>
                        <MenuItem
                          isDisabled={individual?.status !== 'ACTIVE'}
                          onClick={onOpenPauseIndividual}
                        >
                          Pause account
                        </MenuItem>
                        {individual?.status === 'PAUSED' && (
                          <MenuItem onClick={onOpenReactivateIndividual}>
                            Reactivate account
                          </MenuItem>
                        )}
                        {/* Employee-only: a pay calendar is an employee concept, so an admin
                            (COMPANY_ADMIN, PARTNER_ADMIN, ...) has none to change. */}
                        {hasMultiplePayCalendars && (
                          <MenuItem onClick={onOpenReassignPayCalendar}>
                            Change pay calendar
                          </MenuItem>
                        )}
                      </>
                    )}
                  </MenuList>
                </Menu>
                {/* Grant/Restrict Access Buttons as separate buttons, not in dropdown */}
                {individual?.role === 'EMPLOYEE' && individual?.status === 'RESTRICTED' && (
                  <GrantAccessButton
                    employeeId={individual.id}
                    employeeName={`${individual.firstName} ${individual.lastName}`}
                    onGrant={async () => {
                      setTimeout(async () => {
                        await queryClient.refetchQueries({
                          queryKey: ['fetchIndividual', individualId],
                        });
                        await queryClient.refetchQueries({ queryKey: ['fetchCards'] });
                        await queryClient.refetchQueries({
                          queryKey: ['fetchIndividualConfiguration', individualId],
                        });
                      }, 800); // timeout while LPS runs
                    }}
                  />
                )}
                {individual?.terminationDate && individual?.status === 'LEAVING' && (
                  <RestrictAccessButton
                    employeeId={individual.id}
                    employeeName={`${individual.firstName} ${individual.lastName}`}
                    onRestrict={async () => {
                      setTimeout(async () => {
                        await queryClient.refetchQueries({
                          queryKey: ['fetchIndividual', individualId],
                        });
                        await queryClient.refetchQueries({ queryKey: ['fetchCards'] });
                        await queryClient.refetchQueries({
                          queryKey: ['fetchIndividualConfiguration', individualId],
                        });
                      }, 800); // timeout while LPS runs
                    }}
                  />
                )}
                <Button
                  variant="danger"
                  leftIcon={<DeleteOutline boxSize="20px" />}
                  size="sm"
                  onClick={onOpenDeleteIndividual}
                >
                  Delete
                </Button>
                <Button
                  leftIcon={<EditOutline boxSize="20px" />}
                  size="sm"
                  onClick={handleToggleEditMode}
                >
                  Edit
                </Button>
              </>
            )}
          </Flex>

          <Tabs
            mt="10px"
            index={visibleTabs.findIndex((tab) => tab.label === tabLabel)}
            onChange={handleTabsChange}
          >
            <TabList>
              {visibleTabs.map((tab) => (
                <Tab key={tab.label} isDisabled={editMode}>
                  {tab.displayName}
                </Tab>
              ))}
            </TabList>

            <TabPanels mt="40px">
              {visibleTabs.map((tab) => {
                if (tab.label === 'profile') {
                  return (
                    <TabPanel key={tab.label}>
                      <IndividualProfileTab
                        ref={submitButtonRef}
                        isEmployee={isEmployee}
                        individual={individual}
                        company={company}
                        payCalendar={payCalendar}
                        wallet={wallet}
                        card={card}
                        editMode={editMode && tabLabel === 'profile'}
                        onCloseEditMode={setEditMode.toggle}
                        individualConfiguration={individualConfiguration}
                        companyConfiguration={companyConfiguration}
                      />
                    </TabPanel>
                  );
                }

                if (tab.label === 'payroll') {
                  return (
                    <TabPanel key={tab.label}>
                      {editMode && tabLabel === 'payroll' ? (
                        <IndividualPayrollTabEdit
                          ref={submitButtonRef}
                          individual={individual}
                          onCloseEditMode={setEditMode.toggle}
                        />
                      ) : (
                        <IndividualPayrollTabView individual={individual} />
                      )}
                    </TabPanel>
                  );
                }

                if (tab.label === 'finance') {
                  return (
                    <TabPanel key={tab.label}>
                      <IndividualWalletTab individual={individual} wallet={wallet} />
                    </TabPanel>
                  );
                }

                if (tab.label === 'earnings') {
                  return (
                    <TabPanel key={tab.label}>
                      <IndividualEarningsTab individual={individual} payCalendar={payCalendar} />
                    </TabPanel>
                  );
                }

                if (tab.label === 'reports') {
                  return (
                    <TabPanel key={tab.label}>
                      <IndividualDeductionsReportsTab individual={individual} />
                    </TabPanel>
                  );
                }

                if (tab.label === 'points') {
                  return (
                    <TabPanel key={tab.label}>
                      <IndividualPointsTab individual={individual} />
                    </TabPanel>
                  );
                }

                if (tab.label === 'set-and-spend') {
                  return (
                    <TabPanel key={tab.label}>
                      <IndividualDeductionRequestsTab
                        individual={individual}
                        payCalendar={payCalendar}
                        type="RESERVE"
                      />
                    </TabPanel>
                  );
                }

                if (tab.label === 'savings') {
                  return (
                    <TabPanel key={tab.label}>
                      <IndividualDeductionRequestsTab
                        individual={individual}
                        payCalendar={payCalendar}
                        type="SAVINGS"
                      />
                    </TabPanel>
                  );
                }

                if (tab.label === 'vouchers') {
                  return (
                    <TabPanel key={tab.label}>
                      <VoucherTable individual={individual} />
                    </TabPanel>
                  );
                }

                return null;
              })}
            </TabPanels>
          </Tabs>
        </>
      )}
    </AuthenticatedPageWrapper>
  );
}

export default IndividualProfile;
